import { useQuery } from "@tanstack/react-query";
import { BellRing, Flame, MapPin, PhoneCall } from "lucide-react";
import { useNotify } from "ra-core";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { getSupabaseClient } from "../../providers/supabase/supabase";
import { CALL_SLOTS, LEAD_TEMPERATURES, labelOf } from "../gulaConstants";
import { fullName, type GulaClient } from "../gulaTypes";
import { ALERT_MAX_MINUTES, raceClock, shouldAlert } from "../leadAge";
import { currentCallSlot, useAgents } from "../useAgents";
import { useNow } from "../useNow";
import { useLive } from "../whatsapp/useWhatsapp";
import { bucketForClient } from "./callPlan";
import { takeCalls } from "./useCallPlan";

const SNOOZE_MS = 10 * 60 * 1000;
const SNOOZE_KEY = "gula-new-lead-snooze";

const readSnoozed = (): Record<string, number> => {
  try {
    return JSON.parse(localStorage.getItem(SNOOZE_KEY) ?? "{}");
  } catch {
    return {};
  }
};

const writeSnoozed = (value: Record<string, number>) => {
  try {
    localStorage.setItem(SNOOZE_KEY, JSON.stringify(value));
  } catch {
    // private window: the snooze lasts until the page reloads
  }
};

/** Free form leads that just came in, live for the whole team. */
const useNewLeads = () => {
  const query = useQuery({
    queryKey: ["gula-new-leads"],
    queryFn: async () => {
      const since = new Date(
        Date.now() - ALERT_MAX_MINUTES * 60_000,
      ).toISOString();
      const { data, error } = await getSupabaseClient()
        .from("gula_clients_summary")
        .select("*")
        .eq("stage", "new")
        .is("sales_id", null)
        .gte("created_at", since)
        .order("created_at", { ascending: true })
        .limit(20);
      if (error) throw error;
      return (data ?? []) as GulaClient[];
    },
    refetchInterval: 20_000,
  });
  const { refetch } = query;
  const reload = useCallback(() => {
    refetch();
  }, [refetch]);
  useLive("gula_clients", reload);
  return { leads: query.data ?? [], ready: query.isFetched };
};

/** A short beep (no audio file needed). */
const beep = () => {
  try {
    const context = new AudioContext();
    [0, 0.25].forEach((start) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.frequency.value = 880;
      gain.gain.setValueAtTime(0.25, context.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(
        0.001,
        context.currentTime + start + 0.2,
      );
      oscillator.connect(gain).connect(context.destination);
      oscillator.start(context.currentTime + start);
      oscillator.stop(context.currentTime + start + 0.2);
    });
  } catch {
    // no sound available: the pop-up is still there
  }
};

const notifyDesktop = (lead: GulaClient) => {
  if (!("Notification" in window) || Notification.permission !== "granted")
    return;
  try {
    new Notification("Lead nuevo: llámalo ya", {
      body: `${fullName(lead)} · tienes 5 minutos`,
      tag: `gula-lead-${lead.id}`,
    });
  } catch {
    // some browsers only allow notifications from a service worker
  }
};

const LeadCall = ({
  lead,
  now,
  onSnooze,
}: {
  lead: GulaClient;
  now: number;
  onSnooze?: (lead: GulaClient) => void;
}) => {
  const { meId } = useAgents();
  const notify = useNotify();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const clock = raceClock(lead.created_at, now);
  const temperature = LEAD_TEMPERATURES.find(
    (option) => option.value === lead.lead_temperature,
  );

  const take = async () => {
    if (meId == null) return;
    setBusy(true);
    try {
      const taken = await takeCalls([lead.id], meId, bucketForClient(lead));
      if (!taken) {
        notify("Se te han adelantado: ya lo ha cogido otra persona", {
          type: "info",
        });
        return;
      }
      notify(`${lead.first_name ?? "Lead"} es tuyo: llámalo ya`, {
        type: "success",
      });
      // On the phone the ficha opens over the day list (with WhatsApp call)
      navigate(
        window.innerWidth < 768
          ? `/?cliente=${lead.id}`
          : `/clientes?cliente=${lead.id}`,
      );
    } catch (error: unknown) {
      notify(error instanceof Error ? error.message : "No se ha podido coger", {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-2 rounded-2xl border-2 bg-card p-3",
        clock.late ? "border-red-500" : "border-lime-500",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-semibold">{fullName(lead)}</p>
          <p className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
            {temperature ? (
              <span className="inline-flex items-center gap-0.5 font-medium text-foreground">
                <Flame className="h-3 w-3" />
                {temperature.label}
              </span>
            ) : null}
            {lead.zone ? (
              <span className="inline-flex items-center gap-0.5">
                <MapPin className="h-3 w-3" />
                {lead.zone}
              </span>
            ) : null}
            {lead.call_slot ? (
              <span>Quiere llamada {labelOf(CALL_SLOTS, lead.call_slot)}</span>
            ) : null}
          </p>
        </div>
        <div className="text-right">
          <p
            className={cn(
              "font-mono text-2xl font-bold tabular-nums",
              clock.late ? "text-red-600" : "text-lime-700 dark:text-lime-300",
            )}
          >
            {clock.label}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {clock.late ? "tarde" : "para llamarle"}
          </p>
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" className="flex-1" disabled={busy} onClick={take}>
          <PhoneCall className="h-4 w-4" />
          Lo llamo yo
        </Button>
        {onSnooze ? (
          <Button size="sm" variant="ghost" onClick={() => onSnooze(lead)}>
            Ahora no
          </Button>
        ) : null}
      </div>
    </div>
  );
};

/** Whether this person gets the pop-ups: a real worker, in call hours. */
const useAlertsOn = () => {
  const { meId, findAgent } = useAgents();
  const me = findAgent(meId);
  return !!me && !me.no_work && currentCallSlot() != null;
};

/**
 * Pop-up for the whole team when a form lead comes in during call hours: a
 * 5-minute countdown and "Lo llamo yo". Whoever takes it first keeps it; for
 * everybody else it disappears.
 */
export const NewLeadAlerts = () => {
  const isOn = useAlertsOn();
  const { leads, ready } = useNewLeads();
  const now = useNow(1000);
  const [snoozed, setSnoozed] = useState(readSnoozed);
  const seen = useRef<Set<string> | null>(null);
  const [permission, setPermission] = useState(
    "Notification" in window ? Notification.permission : "denied",
  );

  const visible = isOn
    ? leads.filter(
        (lead) =>
          shouldAlert(lead, now) && (snoozed[String(lead.id)] ?? 0) < now,
      )
    : [];

  // A sound (and a desktop notification if the tab is hidden) for each new lead
  const latest = useRef(visible);
  latest.current = visible;
  const visibleKey = visible.map((lead) => String(lead.id)).join(",");
  useEffect(() => {
    if (!ready) return;
    const current = latest.current;
    const ids = current.map((lead) => String(lead.id));
    // Leads already waiting when the page opens do not beep
    if (seen.current == null) {
      seen.current = new Set(ids);
      return;
    }
    const fresh = current.filter((lead) => !seen.current?.has(String(lead.id)));
    ids.forEach((id) => seen.current?.add(id));
    if (!fresh.length) return;
    beep();
    if (document.hidden) fresh.forEach(notifyDesktop);
  }, [visibleKey, ready]);

  if (!visible.length) return null;

  const snooze = (lead: GulaClient) => {
    const next = { ...snoozed, [String(lead.id)]: Date.now() + SNOOZE_MS };
    setSnoozed(next);
    writeSnoozed(next);
  };

  const [first, ...rest] = visible;
  return (
    <div className="fixed bottom-24 right-3 z-50 md:bottom-4 md:right-4 flex w-[min(380px,calc(100vw-2rem))] flex-col gap-2 rounded-3xl border bg-background p-3 shadow-2xl">
      <p className="flex items-center gap-2 text-sm font-semibold">
        <BellRing className="h-4 w-4 animate-bounce text-lime-600" />
        Lead nuevo: llámalo antes de 5 minutos
      </p>
      <LeadCall lead={first} now={now} onSnooze={snooze} />
      {rest.length ? (
        <p className="text-xs text-muted-foreground">
          Y {rest.length} más esperando: los ves en Mi jornada.
        </p>
      ) : null}
      {permission === "default" ? (
        <button
          type="button"
          className="text-left text-xs text-muted-foreground underline"
          onClick={() =>
            Notification.requestPermission().then((value) =>
              setPermission(value),
            )
          }
        >
          Avisarme también cuando esté en otra pestaña
        </button>
      ) : null}
    </div>
  );
};

/** Mi jornada: every lead waiting for its first call right now. */
export const NewLeadsNow = () => {
  const { leads } = useNewLeads();
  const now = useNow(1000);
  const waiting = leads.filter((lead) => shouldAlert(lead, now));
  if (!waiting.length) return null;
  return (
    <section className="flex flex-col gap-2 rounded-3xl border-2 border-lime-500 bg-lime-50 p-4 dark:bg-lime-500/10">
      <p className="flex items-center gap-2 font-semibold">
        <BellRing className="h-4 w-4 text-lime-600" />
        Leads nuevos: el primero que llama se lo queda
      </p>
      <div className="grid gap-2 md:grid-cols-2">
        {waiting.map((lead) => (
          <LeadCall key={lead.id} lead={lead} now={now} />
        ))}
      </div>
    </section>
  );
};
